import { type FormEvent, useEffect, useState } from 'react'
import { Link, createFileRoute } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import { ConfirmEmailCard } from '../components/ConfirmEmailCard'
import { Photo } from '../components/Photo'
import { PictureOfferCard } from '../components/PictureOfferCard'
import { SubmissionSheet } from '../components/SubmissionSheet'
import { Avatar, Banner, Button, ButtonLink, Chip, EmptyState, Field, FormError, Label, PageTitle, Spinner } from '../components/ui'
import { cx } from '../lib/cx'
import { type Season, type Submission, api, call, messageOf } from '../lib/api'
import { categoryVoteQuery, feedQuery, leagueQuery, roundsQuery, seasonsQuery } from '../lib/queries'
import { countdown, deadlineLabel, useNow } from '../lib/time'
import { votes } from '../design/tokens'

export const Route = createFileRoute('/l/$slug/')({
  // `post` opens that post's sheet on arrival: the comment email links here
  // (designs/20261019-comments.md §4).
  validateSearch: (s: Record<string, unknown>): { category?: number; post?: number } => ({
    category: typeof s.category === 'number' ? s.category : undefined,
    post: typeof s.post === 'number' ? s.post : undefined,
  }),
  component: ThisWeek,
})

function ThisWeek() {
  const { slug } = Route.useParams()
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const { data: league } = useSuspenseQuery(leagueQuery(slug))
  const { data: seasons } = useSuspenseQuery(seasonsQuery(slug))
  const rounds = useQuery(roundsQuery(slug))
  const now = useNow()
  const [open, setOpen] = useState<Submission | null>(null)

  const round = league.current_round
  const season = seasons[seasons.length - 1]
  if (!round || !season) return <EmptyState title="No round yet">The first round opens as soon as the league is created.</EmptyState>

  // The season is over (the server says so: its last round is final). This
  // is its trophy page until the admin starts the next one (§3).
  if (season.state === 'complete') {
    return <SeasonComplete slug={slug} season={season} isAdmin={league.is_admin} defaultRounds={league.season_rounds} />
  }

  const cats = round.categories ?? []
  const active = cats.find((c) => c.id === search.category) ?? cats[0]
  const submitting = round.status === 'submitting'
  const voting = rounds.data?.find((r) => r.status === 'voting')

  return (
    <>
      <PageTitle eyebrow={`Round ${round.number} of ${season.rounds}`} aside={<Chip tone={submitting ? 'live' : 'done'}>{submitting ? 'Open' : round.status === 'voting' ? 'Voting' : 'Closed'}</Chip>}>
        This week
      </PageTitle>

      {submitting ? (
        <Banner label="Submissions close in" detail={deadlineLabel(round.submit_close_at)} value={countdown(round.submit_close_at, now)} />
      ) : (
        <Banner label="This round" detail="Submissions are closed" value={round.status === 'voting' ? 'VOTE' : 'FINAL'} />
      )}

      <ConfirmEmailCard />
      <PictureOfferCard />

      {voting && voting.number !== round.number && (
        <Link to="/l/$slug/vote" params={{ slug }} className="flex items-center justify-between gap-3 rounded-card border border-line bg-surface px-4 py-3 text-[14.5px]">
          <span>
            <b className="font-semibold">Round {voting.number} is in voting.</b> <span className="text-muted">Hand out your {votes(5)}.</span>
          </span>
          <span aria-hidden className="text-muted">→</span>
        </Link>
      )}

      <CategoryVoteCard slug={slug} />

      {/* The category row (sc-5184): tab-sized chips (40px+), each tab's hit
          area 6px past its chip on every side with the gaps filled, so 52px+
          tall, and 14px of nothing under it before the first photo, so a tap
          that lands a little low switches category or does nothing, and never
          opens a post. Only the sides bleed to the screen's edges. */}
      <div className="-mx-4 -mt-1.5 mb-3.5 flex overflow-x-auto px-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" role="tablist" aria-label="Categories">
        {cats.map((c) => (
          <button key={c.id} type="button" role="tab" className="shrink-0 px-1 py-1.5" aria-selected={c.id === active?.id} onClick={() => navigate({ search: { category: c.id }, replace: true })}>
            <Chip size="tab" tone={c.id === active?.id ? 'selected' : 'default'}>
              {c.name}
              <span className={cx('tabular-nums', c.id === active?.id ? 'opacity-70' : 'text-muted')}>{c.submission_count}</span>
            </Chip>
          </button>
        ))}
      </div>

      {active && <Feed categoryId={active.id} name={active.name} slug={slug} submitting={submitting} onOpen={setOpen} openPost={search.post} />}

      {submitting && active && (
        <ButtonLink to="/l/$slug/post" params={{ slug }} search={{ category: active.id }} variant="primary" className="fixed bottom-[calc(env(safe-area-inset-bottom,0px)+76px)] right-[max(16px,calc(50vw-264px))] z-30 shadow-lift">
          Post
        </ButtonLink>
      )}
      <SubmissionSheet submission={open} canDelete={submitting} onClose={() => setOpen(null)} />
    </>
  )
}

// While the league votes on next week's categories (sc-4082): the two in
// front, how many picks you have left, and the way to the ballot.
function CategoryVoteCard({ slug }: { slug: string }) {
  const q = useQuery(categoryVoteQuery(slug))
  const v = q.data
  if (!v || !v.open) return null
  const lead = [...(v.candidates ?? [])].filter((c) => c.picks > 0).sort((a, b) => b.picks - a.picks).slice(0, 2)
  const left = v.max_picks - v.my_picks
  return (
    <Link to="/l/$slug/next-week" params={{ slug }} className="flex items-center justify-between gap-3 rounded-card border border-line bg-surface px-4 py-3 text-[14.5px]">
      <span className="min-w-0">
        <b className="font-semibold">Pick next week's categories.</b>{' '}
        <span className="text-muted">
          {left > 0 ? `You have ${left} ${left === 1 ? 'pick' : 'picks'} left.` : 'You’ve picked.'}
          {lead.length > 0 && ` In front: ${lead.map((c) => c.name).join(' and ')}.`}
        </span>
      </span>
      <span aria-hidden className="text-muted">→</span>
    </Link>
  )
}

// The trophy page: who won the season, the podium, the final table, and for
// the admin the way to the next season. Champion and podium come from the
// server, so the app and iOS agree (designs/20261017-seasons.md §2, §3).
function SeasonComplete({ slug, season, isAdmin, defaultRounds }: { slug: string; season: Season; isAdmin: boolean; defaultRounds: number }) {
  const podium = season.podium ?? []
  const champions = podium.filter((p) => p.is_champion)
  const me = champions.some((c) => c.is_me)
  const rounds = season.rounds_final
  return (
    <>
      <PageTitle eyebrow={`${season.name} is complete, ${rounds} ${rounds === 1 ? 'round' : 'rounds'}`}>
        {champions.length === 0 ? "That's a wrap" : `${champions.map((c) => c.manager.display_name).join(' and ')} ${champions.length > 1 ? 'share' : 'takes'} the season`}
      </PageTitle>
      {champions.length > 0 && <Banner label="Final score" detail={me ? 'Well played.' : 'Congratulate them. Then plot revenge.'} value={champions[0].total} />}
      {podium.length > 0 && (
        <ol className="grid gap-1" aria-label="Podium">
          {podium.map((p) => (
            <li key={p.manager.id} className={cx('flex items-center gap-2.5 rounded-field px-2 py-1.5', p.is_me && 'bg-accent-wash')}>
              <span className="w-6 font-score text-[22px] font-bold text-muted" aria-label={`Rank ${p.rank}`}>{p.rank}</span>
              <Avatar src={p.manager.avatar_url} />
              <span className="min-w-0 flex-1 truncate text-[15px]">
                <b className="font-semibold">{p.manager.display_name}</b>
                {p.is_champion && <Chip tone="live" className="ml-2 !py-[2px] !text-[11.5px]">champion</Chip>}
              </span>
              <span className="font-score text-[22px] font-bold">{p.total}</span>
            </li>
          ))}
        </ol>
      )}
      <ButtonLink to="/l/$slug/s/$n/standings" params={{ slug, n: season.number }} variant={isAdmin ? 'quiet' : 'primary'} block>
        See the final table
      </ButtonLink>
      {isAdmin ? (
        <StartSeason slug={slug} next={season.number + 1} defaultRounds={defaultRounds} />
      ) : (
        <p className="text-[14.5px] text-muted">Your league admin can start the next season.</p>
      )}
    </>
  )
}

// "Start season N+1": the admin asks for it; nothing starts on its own in
// the off-season. The name and the length are prefilled and editable.
function StartSeason({ slug, next, defaultRounds }: { slug: string; next: number; defaultRounds: number }) {
  const qc = useQueryClient()
  const [name, setName] = useState(`Season ${next}`)
  const [rounds, setRounds] = useState(String(defaultRounds))
  const start = useMutation({
    mutationFn: () => call(api.POST('/leagues/{slug}/seasons', { params: { path: { slug } }, body: { name: name.trim() || undefined, rounds: Number(rounds) } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['league', slug] }),
  })
  const submit = (e: FormEvent) => {
    e.preventDefault()
    start.mutate()
  }
  return (
    <form onSubmit={submit} className="grid gap-3 rounded-card border border-line bg-surface p-4">
      <Label>Start season {next}</Label>
      <p className="text-[13.5px] text-muted">Round 1 opens the moment you start it, with a clean sheet. Everyone keeps their cats.</p>
      <FormError>{start.isError && messageOf(start.error)}</FormError>
      <Field label="Season name" required maxLength={60} value={name} onChange={(e) => setName(e.target.value)} />
      <Field label="Rounds" type="number" inputMode="numeric" min={1} max={52} required value={rounds} onChange={(e) => setRounds(e.target.value)} />
      <Button type="submit" variant="primary" busy={start.isPending} block>Start season {next}</Button>
    </form>
  )
}

function Feed({ categoryId, name, slug, submitting, onOpen, openPost }: { categoryId: number; name: string; slug: string; submitting: boolean; onOpen: (s: Submission) => void; openPost?: number }) {
  const feed = useQuery(feedQuery(categoryId))
  const navigate = Route.useNavigate()
  const items = feed.data?.submissions ?? []
  const linked = openPost != null ? items.find((s) => s.id === openPost) : undefined
  useEffect(() => {
    if (!linked) return
    onOpen(linked)
    navigate({ search: (prev) => ({ ...prev, post: undefined }), replace: true })
  }, [linked, onOpen, navigate])
  if (feed.isPending) return <div className="grid place-items-center py-10 text-muted"><Spinner /></div>
  if (feed.isError) return <EmptyState title="Couldn't load the feed">{messageOf(feed.error)}</EmptyState>
  if (items.length === 0) {
    return (
      <EmptyState
        title={`No ${name.toLowerCase()} yet`}
        action={submitting && <ButtonLink to="/l/$slug/post" params={{ slug }} search={{ category: categoryId }} size="sm">Post the first one</ButtonLink>}
      >
        Be the first. A blurry one still counts.
      </EmptyState>
    )
  }
  return (
    <>
      <ul className="grid grid-cols-2 gap-2.5">
        {items.map((s) => (
          <li key={s.id} className="min-w-0">
            <button type="button" onClick={() => onOpen(s)} className="grid w-full min-w-0 grid-cols-1 gap-1.5 text-left">
              <Photo media={s.media} alt={`${s.pet_name}, posted by ${s.manager.display_name}`} />
              <span className="flex items-center gap-2 text-[13px]">
                <Avatar kind="cat" src={s.pet_photo_url} />
                <span className="min-w-0 truncate">
                  <b className="font-semibold">{s.pet_name}</b> <span className="text-muted">{s.is_mine ? 'you' : s.manager.display_name}</span>
                </span>
                <span className="ml-auto flex flex-none gap-2 text-[12.5px] tabular-nums text-muted">
                  {s.reactions && s.reactions.length > 0 && (
                    <span aria-label={`${s.reactions.reduce((n, r) => n + r.count, 0)} reactions`}>
                      <span aria-hidden>{s.reactions[0].emoji}</span> {s.reactions.reduce((n, r) => n + r.count, 0)}
                    </span>
                  )}
                  {s.comment_count > 0 && (
                    <span aria-label={`${s.comment_count} comments`}>
                      <span aria-hidden>💬</span> {s.comment_count}
                    </span>
                  )}
                </span>
              </span>
            </button>
          </li>
        ))}
      </ul>
      {feed.data.cap != null && (
        <p className="text-center text-[12.5px] text-muted">
          You've posted {feed.data.my_count} of {feed.data.cap} in {name}.
        </p>
      )}
    </>
  )
}
